import React, { forwardRef, ReactElement } from 'react';

import {
  FinishNavigationButton,
  NextNavigationButton,
  PreviousNavigationButton,
} from '../../../components/NavigationButtons';
import { HistoryItem } from '../../../store/reducers/currentSequencedFlowPositionReducer';
import { useAutoProgress } from '../../RestrictNavigation/hooks/useAutoProgress';
import { useSequencedFlowNavigation } from '../useSequencedFlowNavigation';
import { ElementId } from '@ael/core-api';
import { StudentNavigationBar } from '@ael/shared-components-library';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Stack } from 'immutable';

interface NavigationComponentProps {
  history: Stack<HistoryItem>;
  audioPlayer?: ReactElement;
  isDisplayedOnMobile: boolean;
}

export const Navigation = forwardRef(
  ({ history, audioPlayer, isDisplayedOnMobile }: NavigationComponentProps, ref: React.Ref<HTMLDivElement>) => {
    const { goToNext, onFinish, goToPrevious, progressDisabled, autoProgress, displaySequencedFlowContent } =
      useSequencedFlowNavigation({ history });

    const displayContentOrGoToNext = displaySequencedFlowContent
      ? () => displaySequencedFlowContent()
      : goToNext
      ? () => goToNext()
      : undefined;

    const { autoProgressStarted } = useAutoProgress({ autoProgress, onProgress: () => goToNext?.() });

    return (
      <StudentNavigationBar
        type="StudentFull"
        goToNext={goToNext}
        onFinish={onFinish}
        goToPrevious={goToPrevious}
        autoProgressStarted={autoProgressStarted}
        progressDisabled={progressDisabled}
        ref={ref}
      >
        {(autoProgressStarted) => (
          <>
            {audioPlayer && <StudentNavigationBar.ButtonBox>{audioPlayer}</StudentNavigationBar.ButtonBox>}
            <StudentNavigationBar.ButtonBox justifyEnd hasLabels={!isDisplayedOnMobile}>
              <PreviousNavigationButton
                disabled={!goToPrevious}
                onClick={goToPrevious}
                showLabel={!isDisplayedOnMobile}
              />
              {!displayContentOrGoToNext && onFinish ? (
                <FinishNavigationButton
                  disabled={progressDisabled}
                  onClick={onFinish}
                  big
                  displayAutoProgressIndicator={autoProgressStarted}
                  showLabel={!isDisplayedOnMobile}
                  elementId={ElementId.FinishNavigationButton}
                />
              ) : (
                <NextNavigationButton
                  displayAutoProgressIndicator={autoProgressStarted}
                  disabled={displaySequencedFlowContent ? false : progressDisabled || !displayContentOrGoToNext}
                  onClick={displayContentOrGoToNext}
                  big
                  icon={
                    displaySequencedFlowContent ? solid('arrow-up-right-and-arrow-down-left-from-center') : undefined
                  }
                  showLabel={!isDisplayedOnMobile}
                  elementId={ElementId.NextNavigationButton}
                />
              )}
            </StudentNavigationBar.ButtonBox>
          </>
        )}
      </StudentNavigationBar>
    );
  }
);
